<template>
  <div
    class="animated-backdrop"
    @mouseenter="measure"
    @mousemove="handleMouseMove"
    ref="backdropRef"
  >
    <div class="blob-track track-1"><div class="gradient-blob blob-1"></div></div>
    <div class="blob-track track-2"><div class="gradient-blob blob-2"></div></div>
    <div class="blob-track track-3"><div class="gradient-blob blob-3"></div></div>
    <div class="overlay"></div>
    <slot></slot>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const backdropRef = ref(null);

// Mouse-follow is driven by CSS custom properties (--mx/--my) written
// directly to the backdrop element, rAF-throttled to one update per frame.
// No Vue reactivity is involved, so there are no re-renders or per-blob
// style recalculations. The blobs only ever animate `transform` on
// pre-rasterized layers, so nothing is repainted or re-blurred per frame.
let rect = null;
let rafId = null;
let pendingEvent = null;

// Stored in document coordinates (paired with pageX/pageY below) so the
// cached rect stays valid when the page scrolls.
const measure = () => {
  if (!backdropRef.value) return;
  const r = backdropRef.value.getBoundingClientRect();
  rect = {
    left: r.left + window.scrollX,
    top: r.top + window.scrollY,
    width: r.width,
    height: r.height,
  };
};

const handleMouseMove = (e) => {
  pendingEvent = e;
  if (rafId) return; // coalesce bursts: at most one update per frame
  rafId = requestAnimationFrame(() => {
    rafId = null;
    const el = backdropRef.value;
    if (!el || !rect || !pendingEvent) return;
    const mx = (pendingEvent.pageX - rect.left) / rect.width;
    const my = (pendingEvent.pageY - rect.top) / rect.height;
    el.style.setProperty("--mx", mx.toFixed(4));
    el.style.setProperty("--my", my.toFixed(4));
  });
};

// Pause the float animations while the backdrop is off-screen: the blobs
// would otherwise animate (and periodically re-rasterize) for no visible
// benefit.
let blobObserver = null;

onMounted(() => {
  measure();
  window.addEventListener("resize", measure);
  if ("IntersectionObserver" in window && backdropRef.value) {
    blobObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        backdropRef.value.classList.toggle("blobs-paused", !entry.isIntersecting);
      });
    });
    blobObserver.observe(backdropRef.value);
  }
});

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId);
  window.removeEventListener("resize", measure);
  if (blobObserver) {
    blobObserver.disconnect();
    blobObserver = null;
  }
});
</script>

<style scoped>
.animated-backdrop {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  --mx: 0.5;
  --my: 0.5;
}

/* Track wrappers handle the mouse-follow movement (translate only).
   Their left/top is a static base position set once, never animated. */
.blob-track {
  position: absolute;
  will-change: transform;
  transition: transform 0.4s cubic-bezier(0.25, 0, 0.25, 1);
  pointer-events: none;
}

.track-1 {
  left: 10%;
  top: 5%;
  transform: translate(
    calc((var(--mx) - 0.5) * 240px),
    calc((var(--my) - 0.5) * 280px)
  );
}

.track-2 {
  left: 50%;
  top: 40%;
  transform: translate(
    calc((0.5 - var(--mx)) * 200px),
    calc((0.5 - var(--my)) * 240px)
  );
}

.track-3 {
  left: 25%;
  top: 60%;
  transform: translate(
    calc((var(--mx) - 0.5) * 280px),
    calc((0.5 - var(--my)) * 320px)
  );
}

/* The blobs only run the idle float keyframes (transform-only). The soft
   edge comes from the gradient's own falloff instead of filter: blur(), so
   scaling/moving them never triggers blur re-rasterization. */
.gradient-blob {
  position: absolute;
  border-radius: 50%;
  opacity: 0.75;
  animation: float 20s cubic-bezier(0.25, 0, 0.25, 1) infinite;
  will-change: transform;
  pointer-events: none;
}

/* Paused while off-screen (see IntersectionObserver in script). */
.animated-backdrop.blobs-paused .gradient-blob {
  animation-play-state: paused;
}

.blob-1 {
  width: 550px;
  height: 550px;
  background: radial-gradient(
    circle,
    rgba(255, 127, 80, 0.95) 0%,
    rgba(255, 127, 80, 0.5) 45%,
    transparent 70%
  );
  animation-delay: 0s;
}

.blob-2 {
  width: 650px;
  height: 650px;
  background: radial-gradient(
    circle,
    rgba(255, 165, 0, 0.95) 0%,
    rgba(255, 165, 0, 0.5) 45%,
    transparent 70%
  );
  animation-delay: -7s;
}

.blob-3 {
  width: 480px;
  height: 480px;
  background: radial-gradient(
    circle,
    rgba(255, 159, 74, 0.95) 0%,
    rgba(255, 159, 74, 0.5) 45%,
    transparent 70%
  );
  animation-delay: -14s;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 1;
}

/* Slotted content goes above overlay */
.animated-backdrop > :not(.blob-track):not(.overlay) {
  position: relative;
  z-index: 2;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0);
  }
  33% {
    transform: translate(50px, -60px);
  }
  66% {
    transform: translate(-40px, 50px);
  }
}

/* Reduce animation on low-power devices */
@media (prefers-reduced-motion: reduce) {
  .gradient-blob {
    animation: none;
  }
  .blob-track {
    transition: none;
    transform: none;
  }
}
</style>
